// Report-param grammar for a heatmap question.
//
// Two params carry the block's state, addressed by prefix:
//   hm   how the image is being read: the view, whether clusters are drawn, which stat the answers
//        list reports, and the cluster mask the answers are computed against
//   s    the sort order of the answers list
//
// The `hm` param is a bag rather than a token, because every part of it feeds the request the
// answers are recomputed from -- the cluster mask especially, which is why a change to it is what
// makes the block refetch.

import { isEqual } from 'lodash'

import {
  BLOCK_TYPE_OPTIONS,
  DEFAULT_BLOCK_TYPE,
  DEFAULT_STAT_TYPE,
  DEFAULT_STOP_WORD_LEVEL,
  DEFAULT_VIEW_MODE,
  VIEW_MODE_OPTIONS
} from '@aytm/app/stats/page/question-views/hm/components/controls/lib/constants'
import {
  DEFAULT_SORT_ORDER,
  decodeSortParam,
  encodeSortParam
} from '@aytm/app/stats/page/question-views/components/lib/sort-param'
import { decodeUncollapsedParam } from '@aytm/app/stats/page/question-views/components/lib/uncollapsed-param'
import { hasClusters, calcClusters } from '@aytm/app/stats/page/question-views/hm/lib/clusters'

export const DEFAULT_CLUSTERS_MODE = 'clusters-on'

export { DEFAULT_SORT_ORDER }

// A heatmap with nothing to contour has no heat to draw, so it opens on the pins themselves.
export const DOTS_VIEW_MODE = 'dots'

// the views drawn from the clusters rather than from the pins, which a mask change redraws
export const CLUSTER_VIEW_MODES = ['overlayclusters', 'heatmapclusters']

const BLOCK_TYPES = BLOCK_TYPE_OPTIONS.map(option => option.value)

const VIEW_MODES = VIEW_MODE_OPTIONS.map(option => option.value)

export const HM_DEFAULTS = {
  viewMode: DEFAULT_VIEW_MODE,
  clustersMode: DEFAULT_CLUSTERS_MODE,
  statType: DEFAULT_STAT_TYPE,
  blockType: DEFAULT_BLOCK_TYPE,
  stopWordLevel: DEFAULT_STOP_WORD_LEVEL,
  config: { applyClustersMask: false }
}

// A question whose answers drew no contours has no heat map to show.
export const hasEmptyContours = answer => 0 === answer?.heatmap?.contours?.length

export const calcDefaultViewMode = answer => (hasEmptyContours(answer) ? DOTS_VIEW_MODE : DEFAULT_VIEW_MODE)

// A view the question can still be read through: one of the offered tokens, and -- for the views
// drawn from the clusters rather than from the pins -- only where there are clusters to draw them
// from. It is the same reconciliation the view-mode selector makes when it drops the cluster-bound
// options from a question that has none.
const isViewDrawable = (viewMode, hasClusters) =>
  VIEW_MODES.includes(viewMode) && (hasClusters || !CLUSTER_VIEW_MODES.includes(viewMode))

// How the answers list reports the pins: by pin or by cluster, and which clusters. A question with
// no clusters hides the control that sets it, so it reports the pins by pin whatever was stored --
// a stored cluster table would otherwise open empty with no way back.
export const GROUPING_DEFAULTS = {
  statType: HM_DEFAULTS.statType,
  blockType: HM_DEFAULTS.blockType,
  stopWordLevel: HM_DEFAULTS.stopWordLevel
}

const resolveGrouping = hm => ({
  statType: hm?.statType || HM_DEFAULTS.statType,
  blockType: hm?.blockType && BLOCK_TYPES.includes(hm.blockType) ? hm.blockType : HM_DEFAULTS.blockType,
  stopWordLevel: hm?.stopWordLevel || HM_DEFAULTS.stopWordLevel
})

// What the block opens on. A stored bag says how the researcher last left it; anything it does not
// name -- or names with a view or a cluster grouping the question no longer offers -- falls to the
// default. A stored view the renderer cannot draw would otherwise leave the block with nothing to
// show and no way back, since the bag outlives the session it was written in.
export const resolveHmState = ({ hm } = {}, answer, hasClusters = false) => ({
  viewMode: isViewDrawable(hm?.viewMode, hasClusters) ? hm.viewMode : calcDefaultViewMode(answer),
  clustersMode: hm?.clustersMode || HM_DEFAULTS.clustersMode,
  ...(hasClusters ? resolveGrouping(hm) : GROUPING_DEFAULTS),
  // no stored bag at all -- absent, or cleared back to null once the block was left as it opens --
  // is the default mask; a bag that simply does not name one is the researcher having turned it off
  config: (null == hm ? HM_DEFAULTS.config : hm.config) || {}
})

// A stored grouping the block could not keep: the question has no clusters, so it opened on the
// defaults instead, and the report has to be told or it goes on holding the grouping it cannot show.
export const isStoredGroupingDropped = ({ hm } = {}, hasClusters = false) =>
  !hasClusters &&
  null != hm &&
  Object.entries(GROUPING_DEFAULTS).some(([key, value]) => null != hm[key] && hm[key] !== value)

// A stored view the block could not keep: a token it does not know, or a view drawn from clusters
// the question does not have. The block opened on the default view instead, and the report has to
// be told. Apart from the grouping because the default view depends on the answers, so the write
// waits for them.
export const isStoredViewDropped = ({ hm } = {}, hasClusters = false) =>
  null != hm?.viewMode && !isViewDrawable(hm.viewMode, hasClusters)

export const resolveSortOrder = section => decodeSortParam(section, { custom: false })

// The answers list order is stored only once it leaves the order the list opens on.
export const calcSortParam = sortOrder => encodeSortParam(sortOrder, { custom: false })

// What the block is worth storing: nothing at all while the image is read the way it opens.
export const calcHmSection = ({ viewMode, clustersMode, statType, blockType, stopWordLevel, config }, answer) => {
  const state = { viewMode, clustersMode, statType, blockType, stopWordLevel, config }

  const defaults = { ...HM_DEFAULTS, viewMode: calcDefaultViewMode(answer) }

  // a config naming no mask has the mask off, the same as the opening config that names it off
  const compared = { ...state, config: { applyClustersMask: false, ...config } }

  return { hm: isEqual(compared, defaults) ? null : state }
}

// The state a block opens in, read off the params bag it is handed. The image reading resolves
// against the answer the block last settled on; the clusters it draws, against the one in hand.
export const resolveReportParams = ({ params = {}, settledAnswer, answer, visualPosition } = {}) => {
  const hm = resolveHmState(params, settledAnswer, hasClusters(visualPosition))

  return {
    ...hm,
    sortOrder: resolveSortOrder(params.s),
    uncollapsed: decodeUncollapsedParam(params.cb),
    clusters: calcClusters({ visualPosition, answer, config: hm.config })
  }
}
